import React from 'react';
import { Link2, MapPin } from 'lucide-react';
import verifiedIcon from '../assets/verified.svg';
import AppBannerBackdrop from './AppBannerBackdrop';

export interface HeaderProps {
  companyName: string;
  location: string;
  websiteText: string;
  servicesText: string;
  industriesText: string;
  introCallCertified?: boolean;
  showLogo?: boolean;
  headerAction?: React.ReactNode;
  contentAction?: React.ReactNode;
}

function normalizeWebsiteUrl(raw: string) {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  return `https://${trimmed}`;
}

function getDomainFromWebsite(raw: string) {
  const normalized = normalizeWebsiteUrl(raw);
  if (!normalized) return '';
  try {
    const host = new URL(normalized).hostname;
    return host.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function getInitials(name: string) {
  const withoutParens = (name ?? '').replace(/\([^)]*\)/g, ' ');
  const parts = withoutParens
    .split(/\s+/)
    .map((p) => p.trim())
    .filter((p) => /[A-Za-z]/.test(p));

  if (parts.length === 0) return 'OP';

  if (parts.length === 1) {
    const caps = parts[0].match(/[A-Z]/g) ?? [];
    if (caps.length >= 2) return caps.join('').slice(0, 3);
    const letters = parts[0].match(/[A-Za-z]/g) ?? [];
    return letters.slice(0, 2).map((l) => l.toUpperCase()).join('') || 'OP';
  }

  const initials = parts.flatMap((word) => {
    const caps = word.match(/[A-Z]/g) ?? [];
    if (caps.length >= 2) return caps;
    return [(word.match(/[A-Za-z]/)?.[0] ?? '').toUpperCase()].filter(Boolean);
  });
  return initials.join('').slice(0, 3) || 'OP';
}

export default function Header({
  companyName,
  location,
  websiteText,
  servicesText,
  industriesText,
  introCallCertified = false,
  showLogo = true,
  headerAction,
  contentAction,
}: HeaderProps) {
  const [logoFailed, setLogoFailed] = React.useState(false);
  const domain = getDomainFromWebsite(websiteText);
  const initials = getInitials(companyName);
  const logoSrc = domain ? `https://cdn.brandfetch.io/${domain}/w/120/h/80` : '';

  return (
    <div className="w-full">
      {/* Full-width wave banner with the identity inside, matching the PDF (BPP-231) */}
      <div
        className="relative h-banner w-full overflow-hidden rounded-[12px] border border-[rgba(19,37,67,0.12)]"
        style={{ background: '#0C1930' }}
      >
        <AppBannerBackdrop />

        {/* Overlaid top-right rather than taking a row of its own, so the banner matches
            the interim banner's height and download treatment (BPP-260 follow-up). */}
        {headerAction && (
          <div className="absolute right-[18px] top-[18px] z-10" data-pdf-exclude>
            {headerAction}
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 flex flex-col gap-[14px] px-[24px] pb-[24px]">
          <div className="relative flex items-center gap-[14px]">
            <div className="flex h-[56px] w-[56px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-white">
              {showLogo && domain && !logoFailed ? (
                <img
                  id="provider-logo-img"
                  src={logoSrc}
                  alt={companyName || 'Logo'}
                  loading="lazy"
                  crossOrigin="anonymous"
                  className="block max-h-[38px] max-w-[42px] object-contain object-center"
                  onError={() => setLogoFailed(true)}
                />
              ) : (
                <span
                  className="font-['Roboto_Slab',serif] text-[20px] font-bold text-[#132543]"
                  style={{ fontVariationSettings: "'wdth' 100" }}
                >
                  {initials}
                </span>
              )}
            </div>

            <p
              className="min-w-0 truncate font-['Roboto_Slab',serif] text-[32px] font-bold leading-[40px] text-white"
              style={{ fontVariationSettings: "'wdth' 100", letterSpacing: '0.288px' }}
            >
              {companyName}
            </p>
            {introCallCertified && (
              <span className="relative group inline-flex shrink-0 items-center">
                <img src={verifiedIcon} alt="BluWave certified" className="size-[30px]" />
                <span className="pointer-events-none absolute bottom-full left-1/2 mb-2 w-max -translate-x-1/2 rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                  This provider has been BluWave certified
                </span>
              </span>
            )}
          </div>

          {(!!location || !!websiteText) && (
            <div className="relative flex flex-wrap items-center gap-[8px]">
              {/* Solid white border + white text per BPP-231 (matches the PDF banner chips) */}
              {!!location && (
                <span className="inline-flex items-center gap-[6px] rounded-full border border-white bg-white/[0.10] px-[11px] py-[5px] font-['Inter',sans-serif] text-[12.5px] font-medium text-white">
                  <MapPin size={13} />
                  {location}
                </span>
              )}
              {!!websiteText && (
                <a
                  className="inline-flex items-center gap-[6px] rounded-full border border-white bg-white/[0.10] px-[11px] py-[5px] font-['Inter',sans-serif] text-[12.5px] font-medium text-white transition-colors hover:bg-white/[0.24]"
                  href={normalizeWebsiteUrl(websiteText)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Link2 size={13} />
                  Website
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {contentAction && (
        <div className="mt-[10px] flex justify-end" data-pdf-exclude>
          {contentAction}
        </div>
      )}
    </div>
  );
}
